import { LevelHudLayout } from 'Layouts/LevelHud/LevelHudLayout';
import BaseUIHandler from 'System/UI/BaseUIHandler';
import { PopupID } from 'State/UI/PopupID';
import BaseUISystem, { UIEvent } from 'System/UI/BaseUISystem';
import RelativePosition from 'Component/Render/RelativePosition';
import { LEVEL_START } from 'System/Card/Task/LevelIntroTask';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Interactable from 'Component/Interactable';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { IGameState } from 'State/Game/IGameState';
import { LevelStatus } from 'State/Level/LevelStatus';
import Renderable from 'Component/Renderable';
import { LevelComponent } from 'State/Level/LevelComponent';
import StreakBarTutorial from 'Prototype/UI/Level/StreakBarTutorial';
import Game from 'Game';
import StreakBar from 'Prototype/UI/Level/StreakBar';
import ScoreBar from 'Prototype/UI/Level/ScoreBar';
import CoinBar from 'Prefabs/UI/HUD/CoinBar';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { WinCriteriaType } from 'Constants/WinCriteria';
import PlusFiveBoosterBehavior from 'State/Card/BoosterBehavior/PlusFiveBoosterBehavior';
import { lastFreePlusFiveBooster } from 'Constants/Level';
import { getFreeOrCost } from 'Utility/TextHelper';
import { BoosterType } from 'State/Card/BoosterType';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import CardStateHelper, { isCurrentCardJoker } from 'State/Card/CardStateHelper';
import TextComponent from 'Component/Render/Text';
import TransformComponent from 'Component/Transform';
import { UIPopIn } from 'System/Animation/Clip';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { CardActions } from 'State/Card/CardActions';
import { trackResource } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { isShopSupported } from 'Utility/Platform';
import { UIActions } from 'State/UI/UIActions';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import LevelStateHelper, { isLevelComplete } from 'State/Level/LevelStateHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import GoldBar from 'Prototype/UI/Level/GoldBar';
import OverlayComponent from 'Component/UI/Overlay';
import { Layout } from 'Utility/layout/createLayout';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { LevelTask } from 'State/Level/LevelTask';

type LevelHudProps = {
    system: BaseUISystem;
}

export class LevelHud extends BaseUIHandler {
    public static instance: LevelHud;
    public coinBar: CoinBar;
    private layout: any;
    private streakBar: StreakBar;
    private scoreBar: ScoreBar;
    private goldBar: GoldBar | null = null;
    private isPlayingUndo: boolean = false;

    constructor(props: LevelHudProps) {
        super(PopupID.LEVEL_HUD, props.system, false);

        LevelHud.instance = this;
        this.layout = new LevelHudLayout();
        this.alwaysVisible = true;
        this.system.scene.on(UIEvent.DISABLE_INTERACTIVITY, this.disableInteractivity.bind(this));
        this.system.scene.on(UIEvent.RESTORE_INTERACTIVITY, this.restoreInteractivity.bind(this));
        this.system.scene.on(LEVEL_START, () => {
            this.toggleInteractables(true, false);
        });
        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);
    }

    public getLayout(): Layout {
        return this.layout;
    }

    protected onCreate(): void {
        super.onCreate();

        this.layout.root.addComponent(RelativePosition, { x: 0.5, y: 0.5 });
        this.layout.overlay.addComponent(OverlayComponent, { preventInteraction: true });
        this.element.addChild(this.layout.root);

        this.scoreBar = new ScoreBar({ layout: this.layout, system: this.system });

        this.streakBar = new StreakBar();
        this.streakBar.root.getComponent(TransformComponent).x = -310;
        this.layout.rightTop.addChild(this.streakBar.root);

        this.coinBar = GameObject.Instantiate(CoinBar);
        this.coinBar.container.resize = () => void 0;
        this.coinBar.container.position.set(0);
        this.layout.coinBarContainer.addChild(this.coinBar);

        const state: IGameState = Game.store.getState();
        const isGoldGoal = WorldDataHelper
            .getLevelGoal(state.levelState.levelNumber)
            .some((g) => g.type === WinCriteriaType.CLEAR_GOLD);
        this.layout.goldBarContainer.getComponent(Renderable).visible = isGoldGoal;

        if (isGoldGoal) {
            this.goldBar = new GoldBar({ layout: this.layout });
        }

        this.setupButtons();
    }

    public setupButtons(): void {
        this.layout.settingsButton.on(GameObjectEvent.SELECTED, this.handleSettingsClick);
        this.layout.endLevelButton.on(GameObjectEvent.SELECTED, this.handleEndLevelClick);
        this.layout.jokerButton.on(GameObjectEvent.SELECTED, this.handleJokerClick);
        this.layout.undoButton.on(GameObjectEvent.SELECTED, this.handleUndoClick);
        this.layout.extraCardsButton.on(GameObjectEvent.SELECTED, this.handleExtraCardsClick);

        [
            this.layout.settingsButton,
            this.layout.endLevelButton,
            this.layout.jokerButton,
            this.layout.undoButton,
            this.layout.extraCardsButton,
        ].forEach((gameObject) => {
            gameObject.getComponent(Interactable).toggleable = true;
        });

        [
            this.layout.settingsButton,
            this.layout.endLevelButton,
            this.layout.extraCardsButton,
        ].forEach((gameObject) => {
            applyDefaultButtonAnimation(gameObject);
        });

        applyDefaultButtonAnimation(this.layout.jokerButton, 'joker-01');
        applyDefaultButtonAnimation(this.layout.undoButton, '');
    }

    public handleStateChanged(state: IGameState) {
        if (state.levelState.status === LevelStatus.LOADED) {
            this.handleResize();
        }

        if (!state.levelState.cardsState) return;

        const streakBarRenderable = this.streakBar.root.getComponent(Renderable);
        const extraCardsButtonRenderable = this.layout.extraCardsButton.getComponent(Renderable);
        const endLevelButtonRenderable = this.layout.endLevelButton.getComponent(Renderable);
        const undoContainerRenderable = this.layout.undoContainer.getComponent(Renderable);
        const jokerContainerRenderable = this.layout.jokerContainer.getComponent(Renderable);
        const settingsButtonRenderable = this.layout.settingsButton.getComponent(Renderable);

        const extraCardsUnlocked = state.levelState.components.includes(LevelComponent.BOOSTER_PLUS_FIVE);
        const undoUnlocked = state.levelState.components.includes(LevelComponent.BOOSTER_UNDO);
        const jokerUnlocked = state.levelState.components.includes(LevelComponent.BOOSTER_JOKER);
        const settingsUnlocked = state.levelState.components.includes(LevelComponent.SETTINGS);
        const streakBarUnlocked = state.levelState.components.includes(LevelComponent.BONUS_STREAK);

        if (extraCardsUnlocked && state.levelState.cardsState.inStack.length === 0) {
            this.tryToShowExtraCardsButton(state);
            this.tryToShowEndLevelButton(state);
        } else {
            endLevelButtonRenderable.visible = false;
            extraCardsButtonRenderable.visible = false;
        }

        streakBarRenderable.visible = streakBarUnlocked;
        undoContainerRenderable.visible = undoUnlocked;
        jokerContainerRenderable.visible = jokerUnlocked && (!Boolean(
            state.levelState.tasks.find((e) => e.task === LevelTask.FTUX_BOOSTER_JOKER)
        ));
        settingsButtonRenderable.visible = settingsUnlocked;

        if (undoUnlocked) {
            this.updateUndoButton(state);
        }

        if (jokerUnlocked) {
            this.updateJokerButton(state);
        }

        if (StreakBarTutorial.shouldShow(state)) {
            const tutorial = new StreakBarTutorial({ streakBar: this.streakBar });
            this.element.addChild(tutorial.root);
            this.element.addChild(this.streakBar.root);
        }

        this.goldBar?.handleStateChanged(state);
        super.handleStateChanged(state);
    }

    public restoreInteractivity(): void {
        super.restoreInteractivity();

        const state: IGameState = Game.store.getState();
        this.updateUndoButton(state);
        this.updateJokerButton(state);
    }

    public destroy(): void {
        super.destroy();
        RenderSystem.off(RenderSystemEvent.RESIZE);
    }

    private tryToShowExtraCardsButton(state: IGameState): void {
        if (state.levelState.cardsState.inStack.length !== 0 || state.levelState.cardsState.inPlay.length === 0) return;

        const extraCardsButtonRenderable = this.layout.extraCardsButton.getComponent(Renderable);
        const previousState = Game.store.getPreviousState();
        const plusFiveBehavior = new PlusFiveBoosterBehavior();

        if (
            !extraCardsButtonRenderable.visible ||
            plusFiveBehavior.getInventoryItem(state) !== plusFiveBehavior.getInventoryItem(previousState)
        ) {
            // Update price (if less than level 3 text should be 'Free')
            const text = state.levelState.levelNumber > lastFreePlusFiveBooster ?
                getFreeOrCost(
                    state.levelState.boosterPrices[BoosterType.PLUS_FIVE],
                    state.playerState.inventory[BoosterType.PLUS_FIVE],
                )
                : Game.locale.getText('Free');

            extraCardsButtonRenderable.visible = true;
            const extraCardsTextTextComponent: TextComponent = this.layout.extraCardsText.getComponent(TextComponent);
            extraCardsTextTextComponent.text = text;
            StartAnimationClip(new UIPopIn(this.layout.extraCardsButton));

            const isFree = state.levelState.levelNumber < lastFreePlusFiveBooster ||
                state.playerState.inventory[BoosterType.PLUS_FIVE] > 0;

            if (isFree) {
                this.layout.extraCardsText.container.maxWidth = 100;
            } else {
                this.layout.extraCardsText.container.maxWidth = 72;
            }

            this.layout.extraCardsCoin.getComponent(Renderable).visible = !isFree;
            this.layout.extraCardsText.getComponent(TransformComponent).x = isFree ? 0 : -18;
            extraCardsTextTextComponent.anchor.x = isFree ? 0.5 : 0;
        }
    }

    private tryToShowEndLevelButton(state: IGameState): void {
        if (state.levelState.cardsState.inStack.length !== 0 || state.levelState.cardsState.inPlay.length === 0) return;

        const endLevelButtonRenderable = this.layout.endLevelButton.getComponent(Renderable);

        // No possible matches left.
        if (CardStateHelper.getCardMatches(state.levelState.cardsState).length === 0) {
            if (!endLevelButtonRenderable.visible) {
                StartAnimationClip(new UIPopIn(this.layout.endLevelButton));
            }
        } else {
            endLevelButtonRenderable.alpha = 0;
            endLevelButtonRenderable.visible = false;
        }
    }

    private updateUndoButton(state: IGameState): void {
        const undoBoosterPrice = state.levelState.boosterPrices
            ? state.levelState.boosterPrices[BoosterType.UNDO]
            : LevelStateHelper.getBoosterPrice(
                BoosterType.UNDO,
                state.levelState.levelNumber,
                state.levelState.boosterUses,
            );

        this.layout.undoText.getComponent(TextComponent).text =
            getFreeOrCost(undoBoosterPrice, state.playerState.inventory[BoosterType.UNDO]);

            const isFree = state.playerState.inventory[BoosterType.UNDO] > 0;
            if (isFree) {
                this.layout.undoText.container.maxWidth = 100;
                this.layout.undoText.container.maxFontSize = 35;
            } else {
                this.layout.undoText.container.maxWidth = 76;
                this.layout.undoText.container.maxFontSize = 25;
            }

        this.layout.undoCoin.getComponent(Renderable).visible = state.playerState.inventory[BoosterType.UNDO] === 0;

        const interactable = this.layout.undoButton.getComponent(Interactable);
        interactable.tintOnDisable = true;
        interactable.enabled = state.levelState?.cardsState?.undoHistory.length > 0 || false;
    }

    private updateJokerButton(state: IGameState): void {
        const jokerBoosterPrice = state.levelState.boosterPrices
            ? state.levelState.boosterPrices[BoosterType.JOKER]
            : LevelStateHelper.getBoosterPrice(
                BoosterType.JOKER,
                state.levelState.levelNumber,
                state.levelState.boosterUses,
            );

        this.layout.jokerText.getComponent(TextComponent).text =
            getFreeOrCost(jokerBoosterPrice, state.playerState.inventory[BoosterType.JOKER]);

        this.layout.jokerCoin.getComponent(Renderable).visible = state.playerState.inventory[BoosterType.JOKER] === 0;

        const interactable = this.layout.jokerButton.getComponent(Interactable);

        // check if cardsState even exists; sentry issue 2411010798
        if (!interactable.locked && state.levelState.cardsState) {
            interactable.enabled = !isCurrentCardJoker(state.levelState.cardsState);
        }
    }

    private readonly handleSettingsClick = (): void => {
        // prevent opening when level has been completed
        if (isLevelComplete(Game.store.getState().levelState)) return;
        const state = Game.store.getState();
        const gameScene = state.activeScene.result;

        UIActions.showPopup(PopupID.SETTINGS_MENU, {gameScene});
    };

    private readonly handleEndLevelClick = (): void => {
        // prevent opening when level has been completed
        if (isLevelComplete(Game.store.getState().levelState)) return;

        const state = this.system.store.getState();

        UIActions.showPopup(PopupID.LEVEL_POPUP, {
            levelNumber: state.levelState.levelNumber,
            selectedPreBoosters: state.levelState.preBoosters.filter(
                (booster) => state.playerState.inventory[booster] > 0,
            ),
            fail: true,
        });
    };

    private readonly handleJokerClick = (): void => {
        const state = this.system.store.getState();

        if (!state.levelState.cardsState || isCurrentCardJoker(state.levelState.cardsState)) return;

        if (
            state.playerState.inventory[BoosterType.JOKER] > 0 ||
            state.playerState.inventory.coins >= state.levelState.boosterPrices[BoosterType.JOKER]
        ) {
            Game.sdk.gameTracking(FBTrackingEvent.TapOnJokerCard);
            CardActions.playBooster(BoosterType.JOKER);
            trackResource(Game.store.getState());
        } else if (isShopSupported()) {
            UIActions.showPopup(PopupID.SHOP_MENU, {});
        } else {
            UIActions.showPopup(PopupID.NO_COINS_MENU, {});
        }
    };

    private readonly handleUndoClick = (): void => {
        if (this.isPlayingUndo) return;

        const state = this.system.store.getState();

        if (
            state.playerState.inventory[BoosterType.UNDO] > 0 ||
            state.playerState.inventory.coins >= state.levelState.boosterPrices[BoosterType.UNDO]
        ) {
            Game.sdk.gameTracking(FBTrackingEvent.TapOnUndoMove);
            CardActions.playBooster(BoosterType.UNDO);
            trackResource(Game.store.getState());
            this.isPlayingUndo = true;
            setTimeout(() => (this.isPlayingUndo = false), 400);
        } else if (isShopSupported()) {
            UIActions.showPopup(PopupID.SHOP_MENU, {});
        } else {
            UIActions.showPopup(PopupID.NO_COINS_MENU, {});
        }
    };

    private readonly handleExtraCardsClick = (): void => {
        const state = this.system.store.getState();
        const extraCardBehavior = new PlusFiveBoosterBehavior();

        if (extraCardBehavior.isPlayable(state)) {
            track('mExtraCardsBought', PlusFiveBoosterBehavior.cardsToAdd, TrackMethod.ADD);
            CardActions.playBooster(BoosterType.PLUS_FIVE);
            trackResource(Game.store.getState());
        } else if (isShopSupported()) {
            UIActions.showPopup(PopupID.SHOP_MENU, {});
        } else {
            UIActions.showPopup(PopupID.NO_COINS_MENU, {});
        }
    };

    private readonly handleResize = (): void => {
        const { width, height } = PIXIRenderer.renderer;
        this.layout.centerTop.getComponent(TransformComponent).x = width / 2;
        this.layout.rightTop.getComponent(TransformComponent).x = width;
        this.layout.leftBottom.getComponent(TransformComponent).y = height;
        this.layout.centerBottom.getComponent(TransformComponent).position.set(width / 2, height);
        this.layout.rightBottom.getComponent(TransformComponent).position.set(width, height);
        this.layout.overlay.getComponent(TransformComponent).position.set(width / 2, height / 2);
    };
}
